/**
 * The judge's scoring drawn as a gap: each criterion is one track that the AI fills and the
 * agent marks, ordered worst shortfall first. Every row draws, so a run that matched
 * everywhere still has a fold worth opening.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { rankByGap, trackPct, type CriterionScore } from './score-gaps';
import type { ScorePart } from './verdict-readers';

/** One track: the AI is the fill, the agent a mark on the same scale, so the gap reads within the row. */
const CompareTrack = ({
  ai,
  human,
  max,
  tone = 'default',
}: {
  ai: number | null;
  human: number | null;
  max: number;
  tone?: 'default' | 'strong';
}) => {
  // Half the track's height: SVG scales rx and ry independently, so an oversized radius
  // draws a lens rather than a pill.
  const radius = tone === 'strong' ? 4 : 3;
  return (
    <svg className={cn('block w-full', tone === 'strong' ? 'h-2' : 'h-1.5')} aria-hidden>
      <rect width='100%' height='100%' rx={radius} className='fill-gray-300' />
      <rect
        width={`${trackPct(ai, max)}%`}
        height='100%'
        rx={radius}
        className='fill-indigo-500 transition-[width] duration-700 ease-out'
      />
      {human !== null && (
        <rect
          x={`${trackPct(human, max)}%`}
          width='2'
          height='100%'
          rx='1'
          className='-translate-x-px fill-text-heading/70'
        />
      )}
    </svg>
  );
};

/**
 * Signed from the AI's side (agent minus AI, so a negative gap is a win). Weight carries the
 * sign, since the palette has no hue for it: shortfall ink and bold, win indigo, draw grey.
 */
const GapLabel = ({ gap }: { gap: number | null }) => {
  if (gap === null) return null;
  if (gap > 0) return <span className='text-[11px] font-semibold text-text-heading tabular-nums'>&minus;{gap}</span>;
  if (gap < 0) return <span className='text-[11px] font-medium text-indigo-600 tabular-nums'>+{-gap}</span>;
  return <span className='text-[11px] text-gray-800'>match</span>;
};

/** A dimension's headline (both totals and how far apart they are) with its own breakdown nested underneath. */
export const DimensionScore = ({
  label,
  ai,
  human,
  children,
}: {
  label: string;
  ai: number | null;
  human: number | null;
  children?: ReactNode;
}) => (
  <div className='flex flex-col gap-1.5'>
    <div className='flex items-baseline justify-between gap-2'>
      <span className='text-[11px] font-medium tracking-wide text-gray-800 uppercase'>{label}</span>
      <span className='flex items-baseline gap-2 text-xs tabular-nums'>
        <span className='font-medium text-text-heading'>{ai ?? '—'}</span>
        <span className='text-gray-800'>vs {human ?? '—'}</span>
        <GapLabel gap={ai !== null && human !== null ? human - ai : null} />
      </span>
    </div>
    <CompareTrack ai={ai} human={human} max={100} tone='strong' />
    {children}
  </div>
);

const CriterionRow = ({ criterion }: { criterion: CriterionScore }) => (
  <div className='flex items-center gap-3'>
    <span className='w-32 shrink-0 truncate text-[11px] text-gray-800'>{criterion.label}</span>
    <div className='min-w-0 flex-1'>
      <CompareTrack ai={criterion.ai} human={criterion.human} max={criterion.max} />
    </div>
    <span className='w-12 shrink-0 text-right text-[11px] text-gray-800 tabular-nums'>
      {criterion.ai ?? '—'}
      <span className='text-gray-800/60'>/{criterion.max}</span>
    </span>
    <span className='w-8 shrink-0 text-right'>
      <GapLabel gap={criterion.gap} />
    </span>
  </div>
);

/** Every criterion the judge scored, worst shortfall first. */
export const CriterionGaps = ({
  parts,
  subScores,
}: {
  parts: ReadonlyArray<ScorePart>;
  subScores: Record<string, unknown> | null;
}) => {
  const ranked = rankByGap(parts, subScores);
  // Totals with no per-criterion tree happen; say so rather than leave the heading over nothing.
  if (!ranked.length)
    return <p className='text-[11px] leading-4 text-gray-800'>No per-criterion scores came back for this dimension.</p>;

  return (
    <div className='flex flex-col gap-2'>
      {ranked.map((criterion) => (
        <CriterionRow key={criterion.key} criterion={criterion} />
      ))}
    </div>
  );
};

/** The scale both dimensions are drawn on, said once, beside the tracks it explains. */
export const CompareLegend = () => (
  <div className='flex items-center gap-3 text-[11px] text-gray-800'>
    <span className='inline-flex items-center gap-1.5'>
      <span className='inline-block h-1.5 w-4 rounded-full bg-indigo-500' />
      AI
    </span>
    <span className='inline-flex items-center gap-1.5'>
      <span className='inline-block h-2.5 w-0.5 rounded-full bg-text-heading/70' />
      Agent
    </span>
  </div>
);
